import React, { FC } from 'react';
import { useParams } from 'react-router-dom';
import { Trans } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Theme } from '@mui/material';
import Box from '@mui/material/Box';

import { Button, Page } from 'components';
import { usePermissionGroup } from 'features/permissionGroups';
import { hasPermission } from 'utils/auth';
import { selectUserPermissions } from 'store/profile';

import PermissionGroupHeader from './PermissionGroupHeader';
import PermissionGroupScopesTab from './PermissionGroupScopesTab';
import { usePermissionGroupScopes } from './usePermissionGroupScopes';

const PermissionGroupDetailsPage: FC = () => {
  const { id } = useParams();
  const groupId = Number(id);

  const permissions = useSelector(selectUserPermissions);
  const canEdit = hasPermission(permissions, { scope: 'PERMISSION_GROUPS', mode: 'edit' });

  const { data: group, isLoading } = usePermissionGroup(groupId);

  return (
    <Page title={group?.name ?? <Trans i18nKey="KOZOS_JOGOSULTSAGI_CSOPORTOK.TITLE" />} inScroll>
      {!isLoading && group && <PermissionGroupDetailsContent group={group} canEdit={canEdit} />}
    </Page>
  );
};

const styles = {
  root: (t: Theme) => ({
    position: 'sticky',
    top: 0,
    zIndex: 10,
    backgroundColor: 'background.paper',
    py: 1.5,
    pl: 2,
    pr: 2,
    mb: 2,
    boxShadow: t.shadows[2],
  }),
};

const PermissionGroupDetailsContent: FC<{
  group: NonNullable<ReturnType<typeof usePermissionGroup>['data']>;
  canEdit: boolean;
}> = ({ group, canEdit }) => {
  const { sections, grants, handleGrantChange, isDirty, isPending, handleSave } =
    usePermissionGroupScopes(group);

  return (
    <>
      <Box display="flex" alignItems="center" justifyContent="space-between" sx={styles.root}>
        <PermissionGroupHeader group={group} canEdit={canEdit} />
        <Button onClick={handleSave} disabled={!canEdit || !isDirty} loading={isPending}>
          <Trans i18nKey="KOZOS_JOGOSULTSAGI_CSOPORTOK.SAVE" />
        </Button>
      </Box>
      <PermissionGroupScopesTab
        sections={sections}
        grants={grants}
        onChange={handleGrantChange}
        canEdit={canEdit}
      />
    </>
  );
};

export default PermissionGroupDetailsPage;
